// DevHub: header starred-entities dropdown. Extracted from Root.tsx. Wraps
// Backstage's HomePageStarredEntities widget in a render-error boundary so a
// failure shows a graceful fallback instead of blanking the header (H29).
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStarredEntities } from '@backstage/plugin-catalog-react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { HomePageStarredEntities } from '@backstage/plugin-home';
import { commonMessages } from '../../i18n/common';
import { ROUTES } from '../common/constants/routes';
import { useDropdown } from './useDropdown';

// Small render-error boundary so a failure inside the starred-entities widget
// shows a graceful fallback instead of blanking / breaking the header (H29).
class StarredBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { hasError: boolean }
> {
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  state = { hasError: false };

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('StarredEntitiesDropdown render error', error, info);
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

export function StarredEntitiesDropdown() {
  const { open, setOpen, ref } = useDropdown();
  const { t } = useTranslationRef(commonMessages);
  const navigate = useNavigate();
  const { starredEntities } = useStarredEntities();
  const isEmpty = !starredEntities || starredEntities.size === 0;

  const emptyState = (
    <div className="devhub-starred-dropdown__empty">
      <odx-icon className="odx-icon" name="core::favorite" size="lg" />
      <p>{t('starred.empty')}</p>
      <odx-button
        variant="ghost"
        type="button"
        onClick={() => {
          navigate(ROUTES.overview.catalog);
          setOpen(false);
        }}
      >
        {t('starred.browseCatalog')}
      </odx-button>
    </div>
  );

  return (
    <div className="devhub-starred-dropdown" ref={ref}>
      <odx-button
        variant="ghost"
        className={open ? 'is-active' : undefined}
        type="button"
        onClick={() => setOpen(o => !o)}
        title={t('starred.title')}
      >
        <odx-icon className="odx-icon" name="core::favorite" />
      </odx-button>
      {open && (
        <div className="devhub-starred-dropdown__menu">
          {isEmpty ? (
            emptyState
          ) : (
            <StarredBoundary
              fallback={
                <div className="devhub-starred-dropdown__empty">
                  <odx-icon className="odx-icon" name="core::warning" size="lg" />
                  <p>{t('starred.error')}</p>
                </div>
              }
            >
              <HomePageStarredEntities />
            </StarredBoundary>
          )}
        </div>
      )}
    </div>
  );
}
